import * as React from 'react';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// =============================================================================
// sensitive-confirm-dialog.tsx — Confirmation d'une modification (§2.8).
// =============================================================================
// Enveloppe la modale accessible ConfirmDialog (aria-modal, focus, Échap — 0 dép)
// et ajoute, pour les paramètres SENSIBLES, une confirmation TEXTUELLE de type
// « type-to-confirm » : l'administrateur doit RETAPER la clé du paramètre pour
// débloquer le bouton d'application (DBU §2.8 l.1936). Pour un paramètre non
// sensible, la modale reste une confirmation simple (feature « revert » : « Confirmation
// avant »).
//
// AUCUNE règle métier ici : la validation de la valeur reste l'autorité serveur.
// Cette modale ne fait que garder l'INTENTION (confirmation explicite avant PATCH).

export interface SensitiveConfirmDialogProps {
  readonly open: boolean;
  /** Clé du paramètre — texte exact à retaper quand `sensitive` est vrai. */
  readonly paramKey: string;
  /** Vrai ⇒ confirmation textuelle exigée (type-to-confirm). */
  readonly sensitive: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function SensitiveConfirmDialog({
  open,
  paramKey,
  sensitive,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
}: SensitiveConfirmDialogProps): React.JSX.Element | null {
  const [typed, setTyped] = React.useState('');

  // Réinitialise la saisie SYNCHRONEMENT (phase de rendu, PAS un effet post-commit) à chaque
  // (ré)ouverture / changement de paramètre. Pattern « ajuster l'état pendant le rendu » (React
  // docs) : sans lui, la 1re frame après réouverture rendrait avec le `typed` PÉRIMÉ de la
  // confirmation précédente → le bouton « appliquer » d'un paramètre SENSIBLE serait brièvement
  // ACTIVÉ par une confirmation stale (fenêtre d'une frame — faille type-to-confirm + flakiness).
  const [seen, setSeen] = React.useState({ open, paramKey });
  if (seen.open !== open || seen.paramKey !== paramKey) {
    setSeen({ open, paramKey });
    if (open) {
      setTyped('');
    }
  }

  const matches = typed.trim() === paramKey;
  const confirmDisabled = sensitive && !matches;

  const handleConfirm = (): void => {
    if (confirmDisabled) {
      return;
    }
    onConfirm();
  };

  const fieldId = 'sensitive-confirm-input';

  return (
    <ConfirmDialog
      open={open}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      confirmDisabled={confirmDisabled}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      {sensitive ? (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>
            Paramètre sensible : retapez{' '}
            <span className="font-mono font-medium text-foreground">
              {paramKey}
            </span>{' '}
            pour confirmer
          </Label>
          <Input
            id={fieldId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-describedby="sensitive-confirm-help"
          />
          <p id="sensitive-confirm-help" className="text-xs text-muted-foreground">
            Cette confirmation textuelle protège une modification sensible.
          </p>
        </div>
      ) : null}
    </ConfirmDialog>
  );
}
